import { ActorRefFrom, assign, fromPromise, setup } from 'xstate';
import { employerAssociationMachine } from '~/features/provider/settings/employerAssociation.machine';
import { PROVIDER_PAGE_KEYS, vaultPageFromPathname } from '~/routing/routes';
import { notificationState } from '~/services/notifications/notification.machine';
import { providerService } from '~/services/provider/provider.service.machine';
import { Provider } from '~/types';
import { chatbotMachine } from '../../chatbot/chatbot.machine';
import {
  ACADEMICS_STATE,
  AOI_STATE,
  ATTESTATION_STATE,
  BOARD_CERT_STATE,
  CERTIFICATION_STATE,
  CHATBOT_STATE,
  CME_TRAINING_STATE,
  DOCUMENTS_SERVICE,
  DOCUMENTS_STATE,
  EMPLOYER_ASSOCIATION_STATE,
  EMPLOYMENT_STATE,
  LICENSES_STATE,
  MALPRACTICE_STATE,
  MED_LICENSE_STATE,
  NOTIFICATION_STATE,
  PEER_REFERENCE_STATE,
  PRIVILEGES_STATE,
  PROFILE_STATE,
  PROVIDER_SERVICE,
  VAULT_STATE,
} from '../../xstateSystem';
import { academicsMachine } from '../academics/academics.machine';
import { aoiMachine } from '../aoi/aoi.machine';
import { attestationMachine } from '../attestations/attestation.machine';
import { boardCertMachine } from '../board-certification/boardCert.machine';
import { certMachine } from '../certification/cert.machine';
import { cmeTrainingMachine } from '../cme-training/cmeTraining.machine';
import { documentsViewMachine } from '../documents/documents-view.machine';
import { documentsService } from '../documents/documents.service.machine';
import { employmentMachine } from '../employment/employment.machine';
import { licensesFormMachine } from '../licenses/licenseForm.machine';
import { malpracticeMachine } from '../malpractice/malpractice.machine';
import { medLicenseMachine } from '../medical-license/medical_license.machine';
import { peerRefReqMachine } from '../peer-reference-request/peerRefRequestMachine';
import { privilegesMachine } from '../privileges/privileges.machine';
import { refreshVaultProvider } from '../profile/actions';
import { profileFormMachine } from '../profile/profileForm.machine';
import { Events } from './events';
import { Context } from './types';
import { assignCurrentPage, createPageGuard } from './utils';

type WaitForProviderInput = {
  providerService: ActorRefFrom<typeof providerService>;
  // The provider this vault is for. A provider service is fixed to one id for its
  // whole life, so one built for any other id (or a record for one) is not ours.
  providerId?: number;
};
type WaitForProviderOutput = Provider;

export const vaultState = setup({
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
  },
  actors: {
    academicsRef: academicsMachine,
    aoiRef: aoiMachine,
    attestationRef: attestationMachine,
    boardCertRef: boardCertMachine,
    certRef: certMachine,
    chatbotRef: chatbotMachine,
    cmeTrainingRef: cmeTrainingMachine,
    documentsView: documentsViewMachine,
    documentsService: documentsService,
    employerAssociationRef: employerAssociationMachine,
    employmentRef: employmentMachine,
    licensesRef: licensesFormMachine,
    malpracticeRef: malpracticeMachine,
    medLicenseRef: medLicenseMachine,
    notificationsRef: notificationState,
    peerReferenceRef: peerRefReqMachine,
    privilegesRef: privilegesMachine,
    profileRef: profileFormMachine,
    providerService: providerService,
    waitForProvider: fromPromise<WaitForProviderOutput, WaitForProviderInput>(
      async ({ input }: { input: WaitForProviderInput }) => {
        const { providerService, providerId } = input;

        const ownsProvider = (id: number | undefined) => providerId == null || id === providerId;
        if (!ownsProvider(providerService.getSnapshot().context.providerId)) {
          throw new Error('The provider service belongs to a different provider');
        }

        const snapshot = providerService.getSnapshot();
        if (snapshot.matches('$_SUCCESS') && snapshot.context.provider) {
          if (!ownsProvider(snapshot.context.provider.id)) {
            throw new Error('The cached provider belongs to a different provider');
          }
          return snapshot.context.provider;
        }

        return new Promise<Provider>((resolve, reject) => {
          const sub = providerService.subscribe((snapshot) => {
            if (snapshot.matches('$_SUCCESS') && snapshot.context.provider) {
              sub.unsubscribe();
              if (!ownsProvider(snapshot.context.provider.id)) {
                reject(new Error('The loaded provider belongs to a different provider'));
                return;
              }
              resolve(snapshot.context.provider);
            } else if (snapshot.matches('$_ERROR')) {
              sub.unsubscribe();
              reject(snapshot.context.error);
            }
          });
        });
      }
    ),
  },
}).createMachine({
  context: ({ input, spawn, self }) => {
    // Spawn documentsService (viewRef will be passed dynamically)
    const documentsService = spawn('documentsService', {
      id: DOCUMENTS_SERVICE,
      systemId: DOCUMENTS_SERVICE,
      input: {},
    });

    // Get notificationsRef from system (spawned by userState when authenticated)
    const notificationsRef = self.system.get(NOTIFICATION_STATE);

    return {
      sidebarExpanded: false,
      isNotificationsOpen: false,
      provider: undefined,
      userId: input?.userId || undefined,
      userInfoId: input?.userInfoId || undefined,
      providerId: input?.providerId || undefined,
      currentPage: '',

      providerService:
        input?.providerService ||
        spawn('providerService', {
          id: PROVIDER_SERVICE,
          systemId: PROVIDER_SERVICE,
          input: { providerId: input?.providerId },
        }),

      attestationRef: spawn('attestationRef', {
        id: ATTESTATION_STATE,
        systemId: ATTESTATION_STATE,
        input: { attestations: [] },
      }),
      aoiRef: spawn('aoiRef', {
        id: AOI_STATE,
        input: { aois: [] },
      }),
      documentsService: documentsService,
      certRef: spawn('certRef', {
        id: CERTIFICATION_STATE,
        systemId: CERTIFICATION_STATE,
        input: undefined,
      }),
      profileRef: spawn('profileRef', {
        id: PROFILE_STATE,
        input: { currentTab: 0 },
      }),
      academicsRef: spawn('academicsRef', {
        id: ACADEMICS_STATE,
        input: { providerId: 0, educations: [] },
      }),
      cmeTrainingRef: spawn('cmeTrainingRef', {
        id: CME_TRAINING_STATE,
        input: { data: [] },
      }),
      employmentRef: spawn('employmentRef', {
        id: EMPLOYMENT_STATE,
        input: { employments: [] },
      }),
      medLicenseRef: spawn('medLicenseRef', {
        id: MED_LICENSE_STATE,
        systemId: MED_LICENSE_STATE,
        input: { medicalLicenses: [] },
      }),
      notificationsRef,
      licensesRef: spawn('licensesRef', {
        id: LICENSES_STATE,
        systemId: LICENSES_STATE,
        input: { providerId: -1 },
      }),
      malpracticeRef: spawn('malpracticeRef', {
        id: MALPRACTICE_STATE,
        // Reached from the websocket router when a certificate finishes parsing.
        systemId: MALPRACTICE_STATE,
        input: {},
      }),
      peerReferenceRef: spawn('peerReferenceRef', {
        id: PEER_REFERENCE_STATE,
        // MCV-1194: expose via systemId so actions elsewhere can reach this actor
        // with system.get(PEER_REFERENCE_STATE) — same pattern as certRef / academicsRef.
        systemId: PEER_REFERENCE_STATE,
        input: { peerRefs: [] },
      }),
      privilegesRef: spawn('privilegesRef', {
        id: PRIVILEGES_STATE,
        input: {},
      }),
      boardCertRef: spawn('boardCertRef', {
        id: BOARD_CERT_STATE,
        systemId: BOARD_CERT_STATE,
        input: {},
      }),
      chatbotRef: spawn('chatbotRef', {
        id: CHATBOT_STATE,
        input: {},
      }),
      employerAssociationRef: spawn('employerAssociationRef', {
        id: EMPLOYER_ASSOCIATION_STATE,
        systemId: EMPLOYER_ASSOCIATION_STATE,
        input: {},
      }),
    };
  },
  id: VAULT_STATE,
  initial: '$_LOAD_PROVIDER',
  on: {
    ON_REQUEST_PROVIDER: {
      actions: (props) => {
        props.event.actor.send({ type: 'PROVIDER_RESPONSE' });
      },
    },
    ON_SET_PAGE: [
      /* eslint-disable @typescript-eslint/no-explicit-any */
      {
        target: '.$_ACADEMICS_VIEW',
        actions: assignCurrentPage as any,
        guard: createPageGuard({ page: PROVIDER_PAGE_KEYS.academics }) as any,
      },
      {
        target: '.$_AOI_VIEW',
        actions: assignCurrentPage as any,
        guard: createPageGuard({ page: PROVIDER_PAGE_KEYS.aoi }) as any,
      },
      {
        target: '.$_ATTESTATION_VIEW',
        actions: assignCurrentPage as any,
        guard: createPageGuard({ page: PROVIDER_PAGE_KEYS.attestations }) as any,
      },
      {
        target: '.$_BOARD_CERT_VIEW',
        actions: assignCurrentPage as any,
        guard: createPageGuard({ page: PROVIDER_PAGE_KEYS.boardCertifications }) as any,
      },
      {
        target: '.$_CERT_VIEW',
        actions: assignCurrentPage as any,
        guard: createPageGuard({ page: PROVIDER_PAGE_KEYS.certifications }) as any,
      },
      {
        target: '.$_CME_TRAINING_VIEW',
        actions: assignCurrentPage as any,
        guard: createPageGuard({ page: PROVIDER_PAGE_KEYS.cmeTraining }) as any,
      },
      {
        target: '.$_DOCUMENTS_VIEW',
        actions: assignCurrentPage as any,
        guard: createPageGuard({ page: PROVIDER_PAGE_KEYS.documents }) as any,
      },
      {
        target: '.$_EMPLOYMENT_VIEW',
        actions: assignCurrentPage as any,
        guard: createPageGuard({ page: PROVIDER_PAGE_KEYS.employment }) as any,
      },
      {
        target: '.$_LICENSES_FORM',
        actions: assignCurrentPage as any,
        guard: createPageGuard({ page: PROVIDER_PAGE_KEYS.licenses }) as any,
      },
      {
        target: '.$_MALPRACTICE_VIEW',
        actions: assignCurrentPage as any,
        guard: createPageGuard({ page: PROVIDER_PAGE_KEYS.malpractice }) as any,
      },
      {
        target: '.$_MEDICAL_LICENSE_VIEW',
        actions: assignCurrentPage as any,
        guard: createPageGuard({ page: PROVIDER_PAGE_KEYS.medicalLicense }) as any,
      },
      {
        target: '.$_PEER_REFERENCES_REQUEST',
        actions: assignCurrentPage as any,
        guard: createPageGuard({ page: PROVIDER_PAGE_KEYS.peerReferences }) as any,
      },
      {
        target: '.$_PRIVILEGES_VIEW',
        actions: assignCurrentPage as any,
        guard: createPageGuard({ page: PROVIDER_PAGE_KEYS.privileges }) as any,
      },
      {
        target: '.$_PROFILE_FORM',
        actions: assignCurrentPage as any,
        guard: createPageGuard({ page: PROVIDER_PAGE_KEYS.personalProfile }) as any,
      },
      {
        target: '.$_DASHBOARD_VIEW',
        actions: assignCurrentPage as any,
        guard: createPageGuard({ page: PROVIDER_PAGE_KEYS.dashboard }) as any,
      },
      {
        target: '.$_SETTINGS_VIEW',
        actions: assignCurrentPage as any,
        guard: createPageGuard({ page: PROVIDER_PAGE_KEYS.settings }) as any,
      },
      {
        target: '.$_DASHBOARD_VIEW',
        actions: assignCurrentPage as any,
      },
      /* eslint-enable @typescript-eslint/no-explicit-any */
    ],
    ON_TOGGLE_SIDEBAR: {
      actions: assign({ sidebarExpanded: ({ context }) => !context.sidebarExpanded }),
    },
    PROVIDER_UPDATED: {
      // A response for another provider (an earlier account's late reply) is not ours.
      guard: ({ context, event }) =>
        context.providerId == null ||
        (event as { provider: Provider }).provider.id === context.providerId,
      actions: [
        assign({
          provider: ({ event }) => (event as { provider: Provider }).provider,
        }),
        ({ context, event }) =>
          context.profileRef.send({
            type: 'ON_LOAD',
            payload: { provider: (event as { provider: Provider }).provider },
          }),
      ],
    },
    // [MCV-1753] Sent by useVaultPageSync on a notification click that lands on the profile page.
    // PROVIDER_UPDATED then updates the sidebar and profileRef, and only if the server copy changed.
    ON_REFRESH_PROVIDER: {
      actions: ({ context, self }) => {
        void refreshVaultProvider({ providerId: context.providerId, vault: self });
      },
    },
  },
  states: {
    $_LOAD_PROVIDER: {
      invoke: {
        src: 'waitForProvider',
        input: ({ context }: { context: Context }) => ({
          providerService: context.providerService,
          providerId: context.providerId,
        }),
        onDone: {
          actions: [
            assign({
              provider: ({ event }) => event.output,
              providerId: ({ event }) => event.output.id,
            }),
            ({ context, event }) => {
              const provider = event.output;
              const providerId = provider.id;
              const providerCategoryId = provider.category.id;

              // Notify spawned actors of provider context
              context.attestationRef.send({
                type: 'ON_SET_PROVIDER_ID',
                payload: { providerId },
              });
              context.aoiRef.send({
                type: 'ON_SET_PROVIDER_IDS',
                payload: { providerId, providerCategoryId },
              });
              context.academicsRef.send({
                type: 'ON_SET_PROVIDER_ID',
                payload: { providerId },
              });
              context.licensesRef.send({
                type: 'ON_SET_PROVIDER_ID',
                payload: { providerId },
              });
              context.certRef.send({
                type: 'ON_SEND_SHARED_CONTEXT',
                values: { providerId, providerCategoryId },
              });
              context.cmeTrainingRef.send({
                type: 'ON_SEND_SHARED_CONTEXT',
                values: { providerId },
              });
              context.peerReferenceRef.send({
                type: 'ON_SET_PROVIDER_ID',
                payload: { providerId },
              });
              context.privilegesRef.send({
                type: 'ON_SEND_SHARED_CONTEXT',
                values: { providerId, providerCategoryId },
              });
              context.boardCertRef.send({
                type: 'ON_SEND_SHARED_CONTEXT',
                values: { providerId, providerCategoryId },
              });
              context.profileRef.send({
                type: 'ON_LOAD',
                payload: { provider },
              });
            },
            ({ self }) =>
              self.send({
                type: 'ON_SET_PAGE',
                payload: {
                  // [MCV-1753] The path wins over the hash. A hash names a tab ('#dea', '#training'),
                  // not a page, so it used to match no branch and boot fell to $_DASHBOARD_VIEW.
                  page:
                    vaultPageFromPathname(window.location.pathname) ?? PROVIDER_PAGE_KEYS.dashboard,
                  // Send the hash too, as the route sync and the sidebar do, so the academics
                  // entry loads the tab the URL names rather than the one last on screen.
                  hash: window.location.hash,
                },
              }),
          ],
        },
        onError: '$_DASHBOARD_VIEW',
      },
    },
    $_PROFILE_FORM: {
      entry: ({ context }) =>
        context.profileRef.send({
          type: 'ON_LOAD',
          payload: { provider: context.provider as Provider },
        }),
    },
    $_ATTESTATION_VIEW: {
      entry: ({ context }) => context.attestationRef.send({ type: 'ON_LOAD' }),
    },
    $_AOI_VIEW: {
      entry: ({ context }) => context.aoiRef.send({ type: 'ON_LOAD' }),
    },
    $_CERT_VIEW: {
      entry: ({ context }) => context.certRef.send({ type: 'ON_LOAD' }),
    },
    $_LICENSES_FORM: {
      entry: ({ context }) => context.licensesRef.send({ type: 'ON_LOAD' }),
    },
    $_EMPLOYMENT_VIEW: {
      entry: ({ context }) => context.employmentRef.send({ type: 'ON_LOAD' }),
    },
    // [MCV-1753] Academics loaded once at boot and afterwards only on a tab change, so a delegate's
    // edit stayed invisible. The loading check avoids restarting the boot load, or a tab-switch load
    // AcademicDetails' own effect started first.
    // The URL hash names the tab AcademicDetails is about to show (none = Education, '#training' =
    // Training), and the machine outlives the page, so currentTab is only the tab last on screen.
    // A sender that knows the destination hash passes it; the rest fall back to currentTab.
    // When that tab is not the one academics holds, the load goes out as ON_TAB_SWITCH: only that
    // event assigns currentTab, and AcademicDetails sends its own ON_TAB_SWITCH (a second load) for
    // as long as currentTab disagrees with the hash it resolves.
    $_ACADEMICS_VIEW: {
      entry: ({ context, event }) => {
        const academics = context.academicsRef.getSnapshot();
        if (academics.context.loading) return;
        const hash = event.type === 'ON_SET_PAGE' ? event.payload.hash : undefined;
        let tab = academics.context.currentTab;
        if (hash !== undefined) tab = hash.replace('#', '') === 'training' ? 1 : 0;
        if (tab !== academics.context.currentTab) {
          context.academicsRef.send({ type: 'ON_TAB_SWITCH', value: tab });
          return;
        }
        context.academicsRef.send({
          type: 'ON_LOAD',
          payload: { mode: tab === 1 ? 'training' : 'education' },
        });
      },
    },
    $_BOARD_CERT_VIEW: {
      entry: ({ context }) => context.boardCertRef.send({ type: 'ON_LOAD' }),
    },
    $_MEDICAL_LICENSE_VIEW: {
      entry: ({ context }) => context.medLicenseRef.send({ type: 'ON_LOAD' }),
    },
    $_MALPRACTICE_VIEW: {
      entry: ({ context }) => {
        context.malpracticeRef.send({ type: 'ON_LOAD' });
        // [MCV-1766] The insurance tab says when a certificate is being read, which needs the
        // documents and their AI jobs. A plain GET_DOCUMENTS, like the dashboard's, so a loaded
        // list is served from the service's cache.
        if (context.providerId) {
          context.documentsService.send({
            type: 'GET_DOCUMENTS',
            providerId: context.providerId,
          });
        }
      },
    },
    $_DOCUMENTS_VIEW: {
      entry: () => console.log('accountState entering $_DOCUMENTS_VIEW'),
      invoke: {
        src: 'documentsView',
        id: DOCUMENTS_STATE,
        input: ({ context }) => ({
          providerId: context.providerId,
          documentsService: context.documentsService,
        }),
      },
    },
    $_CME_TRAINING_VIEW: {
      entry: ({ context }) => context.cmeTrainingRef.send({ type: 'ON_LOAD' }),
    },
    $_PRIVILEGES_VIEW: {
      entry: ({ context }) => context.privilegesRef.send({ type: 'ON_LOAD' }),
    },
    $_PEER_REFERENCES_REQUEST: {
      entry: ({ context }) => context.peerReferenceRef.send({ type: 'ON_LOAD' }),
    },
    $_DASHBOARD_VIEW: {
      entry: ({ context }) => {
        context.certRef.send({ type: 'ON_LOAD' });
      },
    },
    $_SETTINGS_VIEW: {
      entry: ({ context }) => context.employerAssociationRef.send({ type: 'ON_LOAD' }),
    },
    $_ERROR: {
      entry: [({ event, context }) => console.log('In an error state: ', event, context)],
    },
  },
});
